import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Download, CheckCircle, Loader2, FileSpreadsheet } from 'lucide-react';
import { Modal, Button, Checkbox } from '@/components/ui';
import { ImportPreview } from './ImportPreview';
import {
  parseFile,
  validateImport,
  executeImport,
  downloadTemplate,
} from '@/services/importService';
import { toast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import type {
  ImportValidationResult,
  ImportOptions,
  ImportStep,
} from '@/types/import.types';

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  restaurantId: string;
  onImportComplete?: () => void;
}

export function ImportModal({ open, onClose, restaurantId, onImportComplete }: ImportModalProps) {
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [options, setOptions] = useState<ImportOptions>({
    skipDuplicates: true,
    updateExisting: false,
    createCategories: true,
  });

  const resetState = useCallback(() => {
    setStep('upload');
    setFile(null);
    setResult(null);
    setIsValidating(false);
    setIsImporting(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setIsValidating(true);

    try {
      const data = await parseFile(selectedFile);
      const validationResult = await validateImport(data, restaurantId, options);
      setResult(validationResult);
      setStep('preview');
    } catch (error) {
      toast({
        title: 'Erreur de lecture',
        description: error instanceof Error ? error.message : 'Impossible de lire le fichier',
        variant: 'destructive',
      });
      setFile(null);
    } finally {
      setIsValidating(false);
    }
  }, [restaurantId, options]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
    disabled: isValidating,
  });

  const handleImport = async () => {
    if (!result) return;

    setIsImporting(true);

    try {
      const importResult = await executeImport(result.success, restaurantId, options);

      toast({
        title: 'Import terminé',
        description: `${importResult.imported} importé(s), ${importResult.updated} mis à jour, ${importResult.failed} échoué(s)`,
        variant: importResult.failed > 0 ? 'default' : 'default',
      });

      setStep('done');

      // Auto-close after success
      setTimeout(() => {
        handleClose();
        onImportComplete?.();
      }, 2000);
    } catch (error) {
      toast({
        title: 'Erreur d\'import',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        variant: 'destructive',
      });
      setIsImporting(false);
    }
  };

  const handleBack = () => {
    setStep('upload');
    setFile(null);
    setResult(null);
  };

  const handleOptionChange = (key: keyof ImportOptions, value: boolean) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Import de produits"
      size="lg"
    >
      {step === 'upload' && (
        <div className="space-y-6">
          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={cn(
              'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200',
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600',
              isValidating && 'pointer-events-none opacity-50'
            )}
          >
            <input {...getInputProps()} />

            {isValidating ? (
              <div className="flex flex-col items-center">
                <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  Analyse du fichier...
                </p>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {isDragActive ? 'Déposez le fichier ici' : 'Glissez votre fichier ici'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  ou cliquez pour parcourir
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                  Formats acceptés : .csv, .xlsx
                </p>
              </>
            )}
          </div>

          {/* Template downloads */}
          <div className="flex items-center gap-4">
            <FileSpreadsheet className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Télécharger un modèle :
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate('csv')}
            >
              <Download className="w-4 h-4 mr-2" />
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate('xlsx')}
            >
              <Download className="w-4 h-4 mr-2" />
              Excel
            </Button>
          </div>

          {/* Options */}
          <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <h3 className="font-medium text-gray-900 dark:text-white">
              Options d'import
            </h3>

            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={options.skipDuplicates}
                onCheckedChange={(checked) => handleOptionChange('skipDuplicates', !!checked)}
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Ignorer les doublons
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Les produits avec le même nom ne seront pas importés
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={options.updateExisting}
                onCheckedChange={(checked) => handleOptionChange('updateExisting', !!checked)}
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Mettre à jour les produits existants
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Les produits existants seront mis à jour avec les nouvelles données
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={options.createCategories}
                onCheckedChange={(checked) => handleOptionChange('createCategories', !!checked)}
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Mapper les catégories inconnues
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Les catégories non reconnues seront mappées vers "autre"
                </p>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end">
            <Button variant="outline" onClick={handleClose}>
              Annuler
            </Button>
          </div>
        </div>
      )}

      {step === 'preview' && result && (
        <ImportPreview
          result={result}
          onBack={handleBack}
          onImport={handleImport}
          isImporting={isImporting}
        />
      )}

      {step === 'done' && (
        <div className="text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <p className="text-xl font-medium text-gray-900 dark:text-white">
            Import terminé !
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Fermeture automatique...
          </p>
        </div>
      )}
    </Modal>
  );
}
